import React, { useState, useEffect } from "react";
import RequestHandler from "util/RequestHandler";
import SubFirmTableFilter from "../Filters/SubFirmTableFilter";
import {
  Table,
  Empty,
  Col,
  Row,
  Button,
  Drawer,
  Popconfirm,
  Tag,
  Tooltip,
} from "antd";
import {
  BarsOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import SubFirmAddForm from "../Forms/SubFirmAddForm";
import SubFirmUpdateForm from "../Forms/SubFirmUpdateForm";

const SubFirm = () => {
  const [tableData, setTableData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [tableDataQueryOptions, setTableDataQueryOptions] = useState({
    skip: 0,
    take: 15,
    current: 1,
    filter: "",
  });
  const [addFormVisibility, setAddFormVisibility] = useState(false);
  const [updateFormVisibility, setUpdateFormVisibility] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({});

  useEffect(() => getTableData(), [tableDataQueryOptions]);

  const getTableData = () => {
    const { skip, take, filter } = tableDataQueryOptions;

    setTableData({
      ...tableData,
      loading: true,
    });

    RequestHandler.get(
      `/SubFirm?$count=true${filter}&$skip=${skip}&$top=${take}&$orderBy=addDate desc`
    ).then((response) => {
      setTableData({
        data: response.value,
        total: response["@odata.count"],
        loading: false,
      });
    });
  };

  const paging = (p) => {
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * prevState.take,
        take: p.pageSize,
        current: p.current,
      };
    });
  };

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      skip: 0,
      current: 1,
      take: tableDataQueryOptions.take,
      filter: "",
    });
  };

  const onFinishTableFilter = (query) => {
    setTableDataQueryOptions((prevState) => {
      return { skip: 0, current: 1, take: prevState.take, filter: query };
    });
  };

  const onClickChangeStatus = (id, isActive) => {
    RequestHandler.post("/SubFirm/ChangeStatus", {
      id,
      isActive: !isActive,
    }).then((response) => {
      if (response?.success) {
        getTableData();
      }
    });
  };

  const handleOnClickUpdate = (record) => {
    RequestHandler.get(`/SubFirm/GetById/${record.id}`).then((response) => {
      if (response) {
        setUpdateFormData(response);
        setUpdateFormVisibility(true);
      }
    });
  };

  const handleOnClickDelete = (id) => {
    RequestHandler.delete(`/SubFirm/${id}`).then((response) => {
      if (response?.success) {
        getTableData();
      }
    });
  };

  const onAddSuccess = () => {
    getTableData();
    setAddFormVisibility(false);
  };

  const onUpdateSuccess = () => {
    getTableData();
    setUpdateFormVisibility(false);
  };

  const sendCreatePasswordEmail = (tenantFirmUserId) => {
    RequestHandler.post("/SubFirm/SendCreatePasswordMail/" + tenantFirmUserId);
  };

  const expandedRowRender = (record) => {
    const firmColumns = [
      {
        title: "Bayi Adı",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Bayi Kodu",
        dataIndex: "firmBranchNumber",
        key: "name",
      },
    ];

    return (
      <Table
        columns={firmColumns}
        dataSource={record.firmSubFirmVms}
        pagination={true}
      />
    );
  };

  const columns = [
    {
      title: "Alt Bayi Kodu",
      dataIndex: "subFirmBranchNumber",
    },
    {
      title: "Alt Bayi Ünvanı",
      dataIndex: "name",
    },
    {
      title: "E-posta",
      dataIndex: "email",
    },
    {
      title: "Alt Bayi VKN",
      dataIndex: "vkn",
      render: (data, row) => data ?? "-",
    },
    {
      title: "Alt Bayi TCKN",
      dataIndex: "tcNo",
      render: (data, row) => data ?? "-",
    },
    {
      title: "Telefon Numarası",
      dataIndex: "mobilePhone",
    },
    {
      title: "Şifre Durumu",
      dataIndex: "isPasswordCreated",
      align: "center",
      render: (isPasswordCreated, row, index) => {
        let color = isPasswordCreated ? "green" : "orange";
        let text = isPasswordCreated ? "Oluşturuldu" : "Bekliyor";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Şifre Maili",
      dataIndex: "id",
      align: "center",
      render: (data, row) =>
        row.isPasswordCreated ? null : (
          <Popconfirm
            title="Kullanıcıya tekrar şifrenizi oluşturun maili yollamak istiyor musunuz?"
            okText="Evet"
            cancelText="Hayır"
            onConfirm={(e) => sendCreatePasswordEmail(data)}
          >
            <Button type="link" style={{ marginBottom: 0 }}>
              <MailOutlined style={{ fontSize: "130%" }} />
            </Button>
          </Popconfirm>
        ),
    },
    {
      title: "Durum",
      dataIndex: "isActive",
      align: "center",
      render: (text, row) => {
        const { title, icon } = row.isActive
          ? {
              title: "Kullanıcıyı pasif etmek istiyor musunuz?",
              icon: (
                <Tooltip title="Pasif et">
                  <CheckCircleOutlined
                    style={{ color: "green", fontSize: "130%" }}
                  />
                </Tooltip>
              ),
            }
          : {
              title: "Kullanıcıyı aktif etmek istiyor musunuz?",
              icon: (
                <Tooltip title="Aktif Et">
                  <CloseCircleOutlined
                    style={{ color: "red", fontSize: "130%" }}
                  />
                </Tooltip>
              ),
            };
        return (
          <Popconfirm
            title={title}
            okText="Evet"
            cancelText="Hayır"
            onConfirm={() => onClickChangeStatus(row.id, row.isActive)}
          >
            <a>{icon}</a>
          </Popconfirm>
        );
      },
    },
    {
      title: "Düzenle",
      dataIndex: "id",
      align: "center",
      render: (data, row) => (
        <a onClick={() => handleOnClickUpdate(row)}>
          <EditOutlined style={{ color: "orange", fontSize: "130%" }} />
        </a>
      ),
    },
    {
      title: "Sil",
      dataIndex: "id",
      align: "center",
      render: (data, record) => (
        <Popconfirm
          title="Silmek istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          onConfirm={() => handleOnClickDelete(data)}
        >
          <a>
            <DeleteOutlined style={{ color: "red", fontSize: "130%" }} />
          </a>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Row align="middle">
        <Col xs={24} md={12} lg={16}>
          <p style={{ fontSize: "120%", marginBottom: 0 }}>
            <BarsOutlined /> ALT BAYİ KULLANICILARI
          </p>
        </Col>
        <Col xs={24} md={12} lg={8} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            onClick={() => {
              setAddFormVisibility(true);
            }}
            style={{ marginBottom: 0 }}
          >
            <PlusOutlined /> Alt Bayi Ekle
          </Button>
        </Col>
      </Row>

      <SubFirmTableFilter
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Row id="tableArea">
        <Col span={24}>
          <Table
            columns={columns}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => record.firmSubFirmVms.length > 0,
            }}
            style={{ overflowX: "auto", maxHeight: "100%", width: "100%" }}
            rowKey="id"
            filterable={true}
            sortable={true}
            pageable={true}
            onChange={paging}
            pagination={{
              defaultPageSize: tableDataQueryOptions.take,
              pageSizeOptions: ["15", "25", "50", "100"],
              total: tableData.total,
              current: tableDataQueryOptions.current,
            }}
            dataSource={tableData.data}
            loading={tableData.loading}
            size="small"
          />
        </Col>
      </Row>

      <Drawer
        id="addSubFirm"
        title="Alt Bayi Ekle"
        bodyStyle={{ padding: 24 }}
        width={500}
        onClose={() => setAddFormVisibility(false)}
        visible={addFormVisibility}
        destroyOnClose={true}
      >
        <SubFirmAddForm onSuccess={onAddSuccess} />
      </Drawer>

      <Drawer
        id="updateSubFirm"
        title="Alt Bayi Güncelle"
        bodyStyle={{ padding: 24 }}
        width={500}
        onClose={() => setUpdateFormVisibility(false)}
        visible={updateFormVisibility}
      >
        <SubFirmUpdateForm data={updateFormData} onSuccess={onUpdateSuccess} />
      </Drawer>
    </>
  );
};

export default SubFirm;
